import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
} from '@mui/material'

interface ConfirmBatchDialogProps {
  open: boolean
  batchId: string
  fundName: string
  unitsAssigned: string
  participantCount: number
  submitting: boolean
  /** Chyba potvrzení; musí být UVNITŘ dialogu, jinak ji překryje backdrop. */
  error?: string | null
  onConfirm: (sendEmail: boolean) => void
  onClose: () => void
}

/**
 * Potvrzení přepočtu (fáze 5, jen CEO) — dvojité potvrzení (nevratná akce) +
 * checkbox „Informovat klienta" (rozhodnutí F5-D, default zapnuto, tichý režim).
 * Vzor ConfirmDialog, navíc nese volbu emailu.
 */
export function ConfirmBatchDialog({
  open,
  batchId,
  fundName,
  unitsAssigned,
  participantCount,
  submitting,
  error = null,
  onConfirm,
  onClose,
}: ConfirmBatchDialogProps) {
  const [secondStep, setSecondStep] = useState(false)
  const [sendEmail, setSendEmail] = useState(false)

  // Reset při každém otevření (druhý krok i default emailu).
  useEffect(() => {
    if (open) {
      setSecondStep(false)
      setSendEmail(true)
    }
  }, [open])

  const handleConfirm = () => {
    if (submitting) return
    if (!secondStep) {
      setSecondStep(true)
      return
    }
    onConfirm(sendEmail)
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Potvrdit přepočet {batchId}</DialogTitle>
      <DialogContent>
        {secondStep ? (
          <DialogContentText sx={{ color: 'error.main' }}>
            Opravdu? Tato akce je nevratná. Připíše se {unitsAssigned} jednotek {participantCount}{' '}
            klientům fondu {fundName}, navýší se celkový počet jednotek a vklady přejdou na „Připsáno".
          </DialogContentText>
        ) : (
          <DialogContentText>
            Uloží se přepočet, celkový počet jednotek se navýší o {unitsAssigned} (kumulativně),
            vklady {participantCount} klientů přejdou na „Připsáno".
          </DialogContentText>
        )}
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                disabled={submitting}
              />
            }
            label="Poslat klientům e-mail o připsání jednotek (kopie distributorovi a na klient@apexcapital.cz)"
          />
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" color="inherit" onClick={onClose} disabled={submitting}>
          Zrušit
        </Button>
        <Button
          variant="contained"
          color={secondStep ? 'error' : 'primary'}
          onClick={handleConfirm}
          disabled={submitting}
        >
          {secondStep ? 'Ano, opravdu potvrdit' : 'Potvrdit'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
